import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  CalendarDays,
  CalendarRange,
  Calendar as CalendarIcon,
  LayoutGrid,
  PartyPopper,
  RotateCcw,
  Loader2,
  Plus,
  Pencil,
  Trash2,
  Download,
  Sparkles,
  CheckCircle2,
  Clock,
  Filter,
} from 'lucide-react';
import { useInstitution } from '../../contexts/InstitutionContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissao } from '../../hooks/usePermissao';
import { useToast, useConfirm } from '../../Components/Feedback';
import { Input, Button, Modal, Card, Select } from '../../Components/ui';
import { AREAS, AREA_LABEL } from '../../utils/areas';
import { API_URL } from '../../api/config';

const MESES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

const DIAS_SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

// Cor por tipo de marcação no calendário — vermelho pra dia sem aula (mesmo
// tom de sempre), tom neutro (slate) pra institucional, e a paleta de área
// (dot/bg claro/texto/borda) espelhando as cores já usadas em AREAS
// (utils/areas.js), só que na variante "claro" que os badges de área do resto
// do sistema não têm prontas.
const CORES_AREA = {
  educacional: {
    dot: 'bg-blue-600',
    bgClaro: 'bg-blue-50 dark:bg-blue-900/30',
    texto: 'text-blue-700 dark:text-blue-400',
    borda: 'border-blue-200 dark:border-blue-800/50',
  },
  esportivo: {
    dot: 'bg-green-600',
    bgClaro: 'bg-green-50 dark:bg-green-900/30',
    texto: 'text-green-700 dark:text-green-400',
    borda: 'border-green-200 dark:border-green-800/50',
  },
  cultural: {
    dot: 'bg-purple-600',
    bgClaro: 'bg-purple-50 dark:bg-purple-900/30',
    texto: 'text-purple-700 dark:text-purple-400',
    borda: 'border-purple-200 dark:border-purple-800/50',
  },
  tecnologico: {
    dot: 'bg-orange-600',
    bgClaro: 'bg-orange-50 dark:bg-orange-900/30',
    texto: 'text-orange-700 dark:text-orange-400',
    borda: 'border-orange-200 dark:border-orange-800/50',
  },
  capelania: {
    dot: 'bg-pink-600',
    bgClaro: 'bg-pink-50 dark:bg-pink-900/30',
    texto: 'text-pink-700 dark:text-pink-400',
    borda: 'border-pink-200 dark:border-pink-800/50',
  },
};
const COR_INSTITUCIONAL = {
  dot: 'bg-slate-600',
  bgClaro: 'bg-slate-50 dark:bg-slate-800/40',
  texto: 'text-slate-700 dark:text-slate-300',
  borda: 'border-slate-200 dark:border-slate-700/50',
};
const COR_FERIADO = {
  dot: 'bg-red-600',
  bgClaro: 'bg-red-100 dark:bg-red-900/30',
  texto: 'text-red-700 dark:text-red-400',
  borda: 'border-red-200 dark:border-red-800/50',
};

function corDoEvento(ev) {
  if (ev.origem === 'feriado') return COR_FERIADO;
  if (ev.tipo === 'institucional') return COR_INSTITUCIONAL;
  return CORES_AREA[ev.area] || COR_INSTITUCIONAL;
}

function rotuloTipoEvento(ev) {
  if (ev.origem === 'feriado') return 'Dia sem aula';
  if (ev.tipo === 'institucional') return 'Institucional';
  if (ev.tipo === 'atividade') return `Atividade · ${ev.nome_atividade || '—'}`;
  return `Área · ${AREA_LABEL[ev.area] || ev.area}`;
}

// Todas as datas "YYYY-MM-DD" entre início e fim (inclusive), em UTC explícito
// — mesma convenção de backend/dias-sem-aula.js e relatorio-agenda-anual-pdf.js,
// pra nunca deslizar um dia dependendo do fuso do navegador.
function datasNoIntervalo(dataInicio, dataFim) {
  const [ai, mi, di] = dataInicio.split('-').map(Number);
  const [af, mf, df] = dataFim.split('-').map(Number);
  const atual = new Date(Date.UTC(ai, mi - 1, di));
  const fim = new Date(Date.UTC(af, mf - 1, df));
  const datas = [];
  while (atual <= fim) {
    datas.push(atual.toISOString().split('T')[0]);
    atual.setUTCDate(atual.getUTCDate() + 1);
  }
  return datas;
}

// Agenda institucional: calendário mensal (padrão) e anual unificado — feriados/recessos
// (dias sem aula, ver backend/dias-sem-aula.js: lógica sensível, consultada por
// presenca.js/relatorios.js, tratada aqui SEM nenhuma mudança de
// comportamento) + eventos institucionais/de área/de atividade (ver
// backend/agenda-eventos.js: só informativo, nunca bloqueia chamada nem entra
// em frequência). São duas fontes de dados independentes por baixo — a tela
// só junta as duas no mesmo grid visual.
function DiasSemAula() {
  const { institutionId } = useInstitution();
  const { user } = useAuth();
  const token = localStorage.getItem('authToken');
  const { podeCriar, podeEditar, podeExcluir, podeExportar } = usePermissao('/dias-sem-aula');
  const toast = useToast();
  const confirm = useConfirm();
  const authHeaders = useMemo(
    () => ({
      'x-institution-id': institutionId,
      Authorization: `Bearer ${token}`,
    }),
    [institutionId, token],
  );

  const [diasSemAula, setDiasSemAula] = useState([]);
  const [agendaEventos, setAgendaEventos] = useState([]);
  const [turmas, setTurmas] = useState([]);
  const [, setLoading] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());

  // Modo de visualização: 'mes' (padrão no mês corrente) ou 'ano' (visão anual completa)
  const [viewMode, setViewMode] = useState('mes');
  // Filtro rápido da linha do tempo mensal
  const [filtroCronograma, setFiltroCronograma] = useState('todos');

  // Data de "hoje" do SERVIDOR (GET /api/hoje) — usada pra destacar o dia
  // atual no calendário e posicionar o mês corrente inicialmente.
  const [hojeServidor, setHojeServidor] = useState(null);
  useEffect(() => {
    fetch(`${API_URL}/api/hoje`, { headers: authHeaders })
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => {
        const hoje = body?.data?.hoje ?? body?.hoje;
        if (hoje) {
          setHojeServidor(hoje);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [loadingAction, setLoadingAction] = useState(false);
  const [gerandoPdf, setGerandoPdf] = useState(false);
  const [isPeriodModalOpen, setIsPeriodModalOpen] = useState(false);
  const [periodData, setPeriodData] = useState({ data_inicio: '', data_fim: '', motivo: '' });

  // --- Modal do dia: primeiro mostra a LISTA de eventos daquele dia
  // (qualquer um pode ver, mesmo sem permissão de criar/editar); "Novo
  // evento"/editar troca pra view de formulário (formOpen). ---
  const [selectedDate, setSelectedDate] = useState(null);
  const [dayModalOpen, setDayModalOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  // Descrição do evento só aparece ao CLICAR (não em hover — a tela é usada
  // no celular, onde não existe passar o mouse) — um id expandido por vez,
  // dentro da lista do dia.
  const [descricaoAbertaChave, setDescricaoAbertaChave] = useState(null);
  const [editingEvento, setEditingEvento] = useState(null);
  const [formTipo, setFormTipo] = useState('feriado');
  const [formData, setFormData] = useState({
    motivo: '',
    titulo: '',
    descricao: '',
    area: '',
    id_atividade: '',
    data_fim: '',
  });

  const fetchDiasSemAula = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/dias-sem-aula`, { headers: authHeaders });
      if (!response.ok) throw new Error(`Erro HTTP: ${response.status}`);
      const body = await response.json();
      setDiasSemAula(body?.data ?? (Array.isArray(body) ? body : []));
    } catch (error) {
      console.error('Erro ao buscar dias sem aula:', error);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  const fetchAgendaEventos = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/agenda-eventos`, { headers: authHeaders });
      if (!response.ok) throw new Error(`Erro HTTP: ${response.status}`);
      const body = await response.json();
      setAgendaEventos(body?.data ?? (Array.isArray(body) ? body : []));
    } catch (error) {
      console.error('Erro ao buscar eventos da agenda:', error);
    }
  }, [authHeaders]);

  useEffect(() => {
    fetchDiasSemAula();
  }, [fetchDiasSemAula]);
  useEffect(() => {
    fetchAgendaEventos();
  }, [fetchAgendaEventos]);

  // Turmas ativas — pro seletor de "Evento de Atividade/Turma" (mesma fonte
  // de Turmas.js/GradeTurmas.js). Coordenador de área só escolhe entre as da
  // própria área (mesmo filtro de areasPermitidas abaixo).
  useEffect(() => {
    fetch(`${API_URL}/api/atividades`, { headers: authHeaders })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        const list = data?.data ?? (Array.isArray(data) ? data : []);
        setTurmas(Array.isArray(list) ? list.filter((t) => !t.data_fim) : []);
      })
      .catch(() => {});
  }, [authHeaders]);

  // Coordenador de área só cria/edita evento da própria área; geral/master,
  // qualquer uma — mesmo padrão já usado em Pontos.js pros tipos de
  // atividade interna.
  const ehGeral =
    user?.perfil === 'master' || (user?.perfil === 'coordenador' && !user?.area_coordenacao);
  const areasPermitidas =
    user?.perfil === 'coordenador' && user?.area_coordenacao
      ? AREAS.filter((a) => a.value === user.area_coordenacao)
      : AREAS;
  const turmasPermitidas = useMemo(
    () =>
      user?.perfil === 'coordenador' && user?.area_coordenacao
        ? turmas.filter((t) => t.area === user.area_coordenacao)
        : turmas,
    [turmas, user],
  );

  const tiposEvento = useMemo(
    () => [
      { value: 'feriado', label: 'Feriado / Recesso' },
      ...(ehGeral ? [{ value: 'institucional', label: 'Institucional' }] : []),
      { value: 'area', label: 'Evento de Área' },
      { value: 'atividade', label: 'Evento de Atividade/Turma' },
    ],
    [ehGeral],
  );

  // Mapa "YYYY-MM-DD" -> lista de eventos daquele dia (feriado + agenda),
  // pra colorir a grade e alimentar a lista do modal do dia.
  const eventosPorDia = useMemo(() => {
    const mapa = new Map();
    const registrar = (dataStr, evento) => {
      if (!mapa.has(dataStr)) mapa.set(dataStr, []);
      mapa.get(dataStr).push(evento);
    };
    diasSemAula.forEach((d) => {
      registrar(d.data.split('T')[0], {
        origem: 'feriado',
        id: d.id,
        titulo: d.motivo || 'Dia sem aula',
      });
    });
    agendaEventos.forEach((e) => {
      datasNoIntervalo(e.data_inicio, e.data_fim).forEach((dataStr) => {
        registrar(dataStr, {
          origem: 'agenda',
          id: e.id,
          tipo: e.tipo,
          titulo: e.titulo,
          descricao: e.descricao,
          area: e.area,
          id_atividade: e.id_atividade,
          nome_atividade: e.nome_atividade,
          data_inicio: e.data_inicio,
          data_fim: e.data_fim,
        });
      });
    });
    return mapa;
  }, [diasSemAula, agendaEventos]);

  const eventosDoDia = useCallback(
    (date) => eventosPorDia.get(date.toISOString().split('T')[0]) || [],
    [eventosPorDia],
  );
  const eventosDoDiaSelecionado = selectedDate ? eventosDoDia(selectedDate) : [];

  // Mesma construção local de day.date (new Date(ano, mês, dia)) aplicada à
  // data do servidor, pra comparar like-for-like no isToday abaixo.
  const hojeReferencia = useMemo(() => {
    if (!hojeServidor) return new Date();
    const [ano, mes, dia] = hojeServidor.split('-').map(Number);
    return new Date(ano, mes - 1, dia);
  }, [hojeServidor]);

  // Navegar entre anos
  const navigateYear = (direction) => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev);
      newDate.setFullYear(newDate.getFullYear() + direction);
      return newDate;
    });
  };

  // Navegar entre meses
  const navigateMonthStep = (direction) => {
    setCurrentDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + direction, 1));
  };

  const selectMonth = (monthIndex) => {
    setCurrentDate((prev) => new Date(prev.getFullYear(), monthIndex, 1));
  };

  const goToToday = () => setCurrentDate(new Date());

  // Obter dias do mês — só os dias DAQUELE mês; os espaços antes do dia 1 e
  // depois do último dia ficam em branco (`null`), em vez de mostrar
  // números do mês anterior/seguinte, que confundiam (pareciam pertencer
  // àquele bloco). Preenche só até fechar a última semana, não uma grade
  // fixa de 42 células.
  const getDaysInMonth = useCallback((year, month) => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay();

    const days = [];
    for (let i = 0; i < startDayOfWeek; i++) days.push(null);
    for (let i = 1; i <= daysInMonth; i++) days.push({ date: new Date(year, month, i) });
    while (days.length % 7 !== 0) days.push(null);
    return days;
  }, []);

  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();

  const yearMonths = useMemo(() => {
    const months = [];
    for (let i = 0; i < 12; i++) {
      months.push({ month: i, days: getDaysInMonth(currentYear, i) });
    }
    return months;
  }, [currentYear, getDaysInMonth]);

  const currentMonthDays = useMemo(
    () => getDaysInMonth(currentYear, currentMonth),
    [currentYear, currentMonth, getDaysInMonth],
  );

  // Contagem de eventos por mês no ano selecionado (para badges nas pílulas de meses)
  const contagemPorMes = useMemo(() => {
    const contagens = Array(12).fill(0);
    eventosPorDia.forEach((lista, dataStr) => {
      const [anoStr, mesStr] = dataStr.split('-');
      if (Number(anoStr) === currentYear) {
        const idx = Number(mesStr) - 1;
        if (idx >= 0 && idx < 12) contagens[idx] += lista.length;
      }
    });
    return contagens;
  }, [eventosPorDia, currentYear]);

  // Resumo executivo e cronograma do mês corrente selecionado
  const dadosMesSelecionado = useMemo(() => {
    let diasLetivos = 0;
    let diasSemAulaCount = 0;
    const eventosUnicos = new Set();
    const areasAtivas = new Set();
    const itensCronograma = [];

    currentMonthDays.forEach((dayObj) => {
      if (!dayObj) return;
      const d = dayObj.date;
      const diaSemana = d.getDay();
      const ehFimDeSemana = diaSemana === 0 || diaSemana === 6;
      const lista = eventosDoDia(d);
      const temFeriado = lista.some((ev) => ev.origem === 'feriado');

      if (temFeriado) {
        diasSemAulaCount += 1;
      } else if (!ehFimDeSemana) {
        diasLetivos += 1;
      }

      lista.forEach((ev) => {
        if (ev.origem === 'agenda') {
          eventosUnicos.add(ev.id);
          if (ev.area) areasAtivas.add(ev.area);
        }
        itensCronograma.push({
          date: d,
          dataIso: d.toISOString().split('T')[0],
          evento: ev,
        });
      });
    });

    return {
      diasLetivos,
      diasSemAulaCount,
      totalEventosAgenda: eventosUnicos.size,
      totalAreasAtivas: areasAtivas.size,
      itensCronograma,
    };
  }, [currentMonthDays, eventosDoDia]);

  const cronogramaFiltrado = useMemo(() => {
    return dadosMesSelecionado.itensCronograma.filter(({ evento }) => {
      if (filtroCronograma === 'todos') return true;
      if (filtroCronograma === 'feriado') return evento.origem === 'feriado';
      if (filtroCronograma === 'institucional')
        return evento.origem === 'agenda' && evento.tipo === 'institucional';
      return evento.origem === 'agenda' && evento.area === filtroCronograma;
    });
  }, [dadosMesSelecionado.itensCronograma, filtroCronograma]);

  // Clique em um dia sempre abre a lista de eventos daquele dia — ver
  // qualquer um pode fazer, mesmo sem permissão de criar/editar (a
  // permissão só controla os botões DENTRO do modal, ver podeAgirNoEvento).
  const handleDayClick = (day) => {
    if (!day) return;
    setSelectedDate(day.date);
    setFormOpen(false);
    setEditingEvento(null);
    setDescricaoAbertaChave(null);
    setDayModalOpen(true);
  };

  const handleCloseDayModal = () => {
    setDayModalOpen(false);
    setFormOpen(false);
    setDescricaoAbertaChave(null);
    setSelectedDate(null);
    setEditingEvento(null);
  };

  // Quem pode editar/excluir UM evento específico: feriado segue a
  // permissão geral da tela (podeEditar/podeExcluir); evento de agenda
  // também respeita o escopo de área (mesma regra de agenda-eventos.js —
  // duplicada aqui só pra esconder os botões, o backend é quem garante de
  // verdade).
  const podeAgirNoEvento = (evento) => {
    if (evento.origem === 'feriado') return true;
    if (user?.perfil === 'master') return true;
    if (user?.perfil === 'coordenador' && !user?.area_coordenacao) return true;
    if (user?.perfil === 'coordenador' && user?.area_coordenacao)
      return evento.tipo !== 'institucional' && evento.area === user.area_coordenacao;
    return false;
  };

  // Abre a view de formulário — sem argumento, cria um evento novo nesse
  // dia; com um evento existente, pré-popula pra edição.
  const abrirFormulario = (eventoExistente) => {
    if (eventoExistente) {
      setEditingEvento(eventoExistente);
      if (eventoExistente.origem === 'feriado') {
        setFormTipo('feriado');
        setFormData({
          motivo: eventoExistente.titulo,
          titulo: '',
          descricao: '',
          area: '',
          id_atividade: '',
          data_fim: '',
        });
      } else {
        setFormTipo(eventoExistente.tipo);
        setFormData({
          motivo: '',
          titulo: eventoExistente.titulo,
          descricao: eventoExistente.descricao || '',
          area: eventoExistente.area || '',
          id_atividade: eventoExistente.id_atividade || '',
          data_fim:
            eventoExistente.data_fim !== eventoExistente.data_inicio
              ? eventoExistente.data_fim
              : '',
        });
      }
    } else {
      setEditingEvento(null);
      setFormTipo('feriado');
      setFormData({
        motivo: '',
        titulo: '',
        descricao: '',
        area: areasPermitidas[0]?.value || '',
        id_atividade: '',
        data_fim: '',
      });
    }
    setFormOpen(true);
  };

  const handleSalvarEvento = async (e) => {
    e.preventDefault();
    if (!selectedDate) return;
    const dataStr = selectedDate.toISOString().split('T')[0];

    try {
      if (formTipo === 'feriado') {
        const isEditing = editingEvento?.origem === 'feriado';
        const url = isEditing
          ? `${API_URL}/api/dias-sem-aula/${editingEvento.id}`
          : `${API_URL}/api/dias-sem-aula`;
        const response = await fetch(url, {
          method: isEditing ? 'PUT' : 'POST',
          headers: { ...authHeaders, 'Content-Type': 'application/json' },
          body: JSON.stringify({ data: dataStr, motivo: formData.motivo || null }),
        });
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || errorData.message || `Erro HTTP: ${response.status}`);
        }
        const data = await response.json();
        toast.success(data?.message || 'Salvo com sucesso.');
        fetchDiasSemAula();
      } else {
        if (!formData.titulo.trim()) {
          toast.warning('Informe um título para o evento.');
          return;
        }
        const isEditing = editingEvento?.origem === 'agenda';
        const url = isEditing
          ? `${API_URL}/api/agenda-eventos/${editingEvento.id}`
          : `${API_URL}/api/agenda-eventos`;
        const response = await fetch(url, {
          method: isEditing ? 'PUT' : 'POST',
          headers: { ...authHeaders, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tipo: formTipo,
            titulo: formData.titulo.trim(),
            descricao: formData.descricao || null,
            area: formTipo === 'area' ? formData.area : undefined,
            id_atividade: formTipo === 'atividade' ? formData.id_atividade : undefined,
            data_inicio: dataStr,
            data_fim: formData.data_fim || dataStr,
          }),
        });
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || errorData.message || `Erro HTTP: ${response.status}`);
        }
        const data = await response.json().catch(() => null);
        toast.success(data?.message || 'Salvo com sucesso.');
        fetchAgendaEventos();
      }
      setFormOpen(false);
      handleCloseDayModal();
    } catch (error) {
      console.error('Erro ao salvar evento:', error);
      toast.error(`Erro ao salvar evento: ${error.message}`);
    }
  };

  const handleExcluirEvento = async (evento) => {
    const ok = await confirm(`Remover "${evento.titulo}"?`, {
      title: 'Remover evento',
      confirmText: 'Remover',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      const url =
        evento.origem === 'feriado'
          ? `${API_URL}/api/dias-sem-aula/${evento.id}`
          : `${API_URL}/api/agenda-eventos/${evento.id}`;
      const response = await fetch(url, { method: 'DELETE', headers: authHeaders });
      if (!response.ok) throw new Error(`Erro HTTP: ${response.status}`);
      const data = await response.json().catch(() => null);
      toast.success(data?.message || 'Removido com sucesso.');
      if (evento.origem === 'feriado') fetchDiasSemAula();
      else fetchAgendaEventos();
      setFormOpen(false);
    } catch (error) {
      console.error('Erro ao remover evento:', error);
      toast.error(`Erro ao remover evento: ${error.message}`);
    }
  };

  // Marcar fins de semana automaticamente
  const handleMarcarFinsDeSemana = async () => {
    const ok = await confirm(
      `Deseja marcar todos os sábados e domingos de ${currentYear} como dias sem aula?`,
      { title: 'Marcar fins de semana', confirmText: 'Marcar', tone: 'primary' },
    );
    if (!ok) return;

    setLoadingAction(true);
    try {
      const response = await fetch(`${API_URL}/api/dias-sem-aula/marcar-fins-de-semana`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ano: currentYear }),
      });
      if (!response.ok) throw new Error(`Erro HTTP: ${response.status}`);
      const data = await response.json();
      toast.success(data?.message || 'Fins de semana marcados com sucesso.');
      fetchDiasSemAula();
    } catch (error) {
      console.error('Erro ao marcar fins de semana:', error);
      toast.error(`Erro ao marcar fins de semana: ${error.message}`);
    } finally {
      setLoadingAction(false);
    }
  };

  // Adicionar feriados nacionais automaticamente
  const handleAdicionarFeriadosNacionais = async () => {
    const okFeriados = await confirm(
      `Deseja adicionar todos os feriados nacionais de ${currentYear}?`,
      { title: 'Adicionar feriados', confirmText: 'Adicionar', tone: 'primary' },
    );
    if (!okFeriados) return;

    setLoadingAction(true);
    try {
      const response = await fetch(`${API_URL}/api/dias-sem-aula/adicionar-feriados-nacionais`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ano: currentYear }),
      });
      if (!response.ok) throw new Error(`Erro HTTP: ${response.status}`);
      const data = await response.json();
      toast.success(data?.message || 'Feriados nacionais adicionados com sucesso.');
      fetchDiasSemAula();
    } catch (error) {
      console.error('Erro ao adicionar feriados nacionais:', error);
      toast.error(`Erro ao adicionar feriados nacionais: ${error.message}`);
    } finally {
      setLoadingAction(false);
    }
  };

  // Marcar período de dias sem aula
  const handleMarcarPeriodo = async (e) => {
    e.preventDefault();
    if (!periodData.data_inicio || !periodData.data_fim) {
      toast.warning('Data início e data fim são obrigatórias');
      return;
    }

    setLoadingAction(true);
    try {
      const response = await fetch(`${API_URL}/api/dias-sem-aula/marcar-periodo`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          data_inicio: periodData.data_inicio,
          data_fim: periodData.data_fim,
          motivo: periodData.motivo || 'Período sem aula',
        }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `Erro HTTP: ${response.status}`);
      }
      const data = await response.json();
      toast.success(data?.message || 'Período marcado com sucesso.');
      setIsPeriodModalOpen(false);
      setPeriodData({ data_inicio: '', data_fim: '', motivo: '' });
      fetchDiasSemAula();
    } catch (error) {
      console.error('Erro ao marcar período:', error);
      toast.error(`Erro ao marcar período: ${error.message}`);
    } finally {
      setLoadingAction(false);
    }
  };

  // Imprimir calendário anual em PDF (feriados + eventos da agenda do ano
  // exibido), ver backend/relatorio-agenda-anual-pdf.js — mesmo padrão de
  // download (fetch + blob + <a>) já usado no Relatório de Ponto.
  const handleGerarPdfAnual = async () => {
    setGerandoPdf(true);
    try {
      const response = await fetch(
        `${API_URL}/api/agenda-eventos/relatorio-anual-pdf?ano=${currentYear}`,
        {
          headers: authHeaders,
        },
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Erro ao gerar o calendário em PDF.');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Calendario_${currentYear}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setGerandoPdf(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#F9FAFB] dark:bg-gray-950">
      <main className="p-2 sm:p-4 max-w-7xl mx-auto space-y-3">
        {/* Barra Superior de Navegação, Alternância de Visão e Ações */}
        <Card className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 p-3">
          <div className="flex flex-wrap items-center justify-between sm:justify-start gap-2">
            <div className="flex items-center gap-1.5">
              <Button
                variant="secondary"
                size="sm"
                icon={ChevronLeft}
                onClick={() => navigateYear(-1)}
              >
                Ano Ant.
              </Button>
              <Button variant="primary" size="sm" icon={RotateCcw} onClick={goToToday}>
                Hoje
              </Button>
              <button
                onClick={() => navigateYear(1)}
                className="inline-flex items-center gap-1.5 px-3 h-8 rounded-xl bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs font-medium transition-colors"
              >
                Próx. Ano <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>

            <div className="flex items-center gap-2 px-3 py-1 rounded-xl bg-blue-50/80 dark:bg-blue-950/40 border border-blue-200/60 dark:border-blue-800/50">
              <CalendarIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              <h2 className="text-base sm:text-lg font-bold text-gray-900 dark:text-gray-100">
                {currentYear}
              </h2>
            </div>
          </div>

          {/* Seletor de Modo: Mensal (Padrão) vs Anual */}
          <div className="flex items-center justify-center">
            <div className="inline-flex p-1 rounded-xl bg-gray-100 dark:bg-gray-800 border border-gray-200/70 dark:border-gray-700/70">
              <button
                type="button"
                onClick={() => setViewMode('mes')}
                className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  viewMode === 'mes'
                    ? 'bg-white dark:bg-gray-900 text-blue-600 dark:text-blue-400 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                <CalendarIcon className="w-3.5 h-3.5" />
                Visão Mensal
              </button>
              <button
                type="button"
                onClick={() => setViewMode('ano')}
                className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  viewMode === 'ano'
                    ? 'bg-white dark:bg-gray-900 text-blue-600 dark:text-blue-400 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                <LayoutGrid className="w-3.5 h-3.5" />
                Visão Anual
              </button>
            </div>
          </div>

          {/* Ações Administrativas e Exportação */}
          <div className="flex flex-wrap items-center justify-end gap-1.5">
            {podeCriar && (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  icon={CalendarRange}
                  disabled={loadingAction}
                  onClick={() => setIsPeriodModalOpen(true)}
                  className="!bg-amber-100 dark:!bg-amber-900/30 hover:!bg-amber-200 dark:hover:!bg-amber-900/50 !text-amber-700 dark:!text-amber-400"
                >
                  Período
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  icon={CalendarDays}
                  disabled={loadingAction}
                  onClick={handleMarcarFinsDeSemana}
                  className="!bg-purple-100 dark:!bg-purple-900/30 hover:!bg-purple-200 dark:hover:!bg-purple-900/50 !text-purple-700 dark:!text-purple-400"
                >
                  Fins de Semana
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  icon={PartyPopper}
                  disabled={loadingAction}
                  onClick={handleAdicionarFeriadosNacionais}
                  className="!bg-sky-100 dark:!bg-sky-900/30 hover:!bg-sky-200 dark:hover:!bg-sky-900/50 !text-sky-700 dark:!text-sky-400"
                >
                  Feriados
                </Button>
              </>
            )}
            {podeExportar && (
              <Button
                variant="secondary"
                size="sm"
                icon={Download}
                disabled={gerandoPdf}
                onClick={handleGerarPdfAnual}
              >
                {gerandoPdf ? 'Gerando...' : 'Imprimir Calendário'}
              </Button>
            )}
            {(loadingAction || gerandoPdf) && (
              <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
            )}
          </div>
        </Card>

        {viewMode === 'mes' ? (
          <>
            {/* Barra Rápida de Seleção de Mês + Indicadores do Mês */}
            <Card className="p-3 space-y-3">
              {/* Pílulas dos 12 Meses */}
              <div className="grid grid-cols-3 sm:grid-cols-6 lg:grid-cols-12 gap-1.5">
                {MESES.map((nomeMes, index) => {
                  const ativo = index === currentMonth;
                  const ehMesAtualServidor =
                    hojeReferencia.getFullYear() === currentYear &&
                    hojeReferencia.getMonth() === index;
                  const qtdEventos = contagemPorMes[index];

                  return (
                    <button
                      key={nomeMes}
                      type="button"
                      onClick={() => selectMonth(index)}
                      className={`relative flex items-center justify-between sm:justify-center gap-1 px-2.5 py-2 rounded-xl text-xs font-semibold transition-all border ${
                        ativo
                          ? 'bg-blue-600 text-white border-blue-600 shadow-sm scale-[1.02]'
                          : ehMesAtualServidor
                            ? 'bg-blue-50/70 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800/60 hover:bg-blue-100/70'
                            : 'bg-gray-50 dark:bg-gray-800/60 text-gray-700 dark:text-gray-300 border-gray-200/70 dark:border-gray-800 hover:bg-gray-100 dark:hover:bg-gray-800'
                      }`}
                    >
                      <span className="truncate">{nomeMes}</span>
                      {qtdEventos > 0 && (
                        <span
                          className={`inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold ${
                            ativo
                              ? 'bg-white/25 text-white'
                              : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          {qtdEventos}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Resumo Executivo do Mês */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-1 border-t border-gray-100 dark:border-gray-800">
                <div className="flex items-center gap-2.5 p-2 rounded-xl bg-emerald-50/70 dark:bg-emerald-950/25 border border-emerald-200/60 dark:border-emerald-800/40">
                  <div className="w-8 h-8 rounded-lg bg-emerald-500/15 flex items-center justify-center text-emerald-600 dark:text-emerald-400 shrink-0">
                    <CheckCircle2 className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-[11px] text-emerald-700/80 dark:text-emerald-400/80 font-medium">
                      Dias Letivos Úteis
                    </p>
                    <p className="text-sm font-bold text-emerald-800 dark:text-emerald-300">
                      {dadosMesSelecionado.diasLetivos} dias
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2.5 p-2 rounded-xl bg-red-50/70 dark:bg-red-950/25 border border-red-200/60 dark:border-red-800/40">
                  <div className="w-8 h-8 rounded-lg bg-red-500/15 flex items-center justify-center text-red-600 dark:text-red-400 shrink-0">
                    <PartyPopper className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-[11px] text-red-700/80 dark:text-red-400/80 font-medium">
                      Dias Sem Aula / Recesso
                    </p>
                    <p className="text-sm font-bold text-red-800 dark:text-red-300">
                      {dadosMesSelecionado.diasSemAulaCount} dias
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2.5 p-2 rounded-xl bg-blue-50/70 dark:bg-blue-950/25 border border-blue-200/60 dark:border-blue-800/40">
                  <div className="w-8 h-8 rounded-lg bg-blue-500/15 flex items-center justify-center text-blue-600 dark:text-blue-400 shrink-0">
                    <Sparkles className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-[11px] text-blue-700/80 dark:text-blue-400/80 font-medium">
                      Eventos Programados
                    </p>
                    <p className="text-sm font-bold text-blue-800 dark:text-blue-300">
                      {dadosMesSelecionado.totalEventosAgenda} evento(s)
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2.5 p-2 rounded-xl bg-purple-50/70 dark:bg-purple-950/25 border border-purple-200/60 dark:border-purple-800/40">
                  <div className="w-8 h-8 rounded-lg bg-purple-500/15 flex items-center justify-center text-purple-600 dark:text-purple-400 shrink-0">
                    <Clock className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-[11px] text-purple-700/80 dark:text-purple-400/80 font-medium">
                      Áreas Mobilizadas
                    </p>
                    <p className="text-sm font-bold text-purple-800 dark:text-purple-300">
                      {dadosMesSelecionado.totalAreasAtivas} área(s)
                    </p>
                  </div>
                </div>
              </div>
            </Card>

            {/* Painel Principal: Calendário Mensal Detalhado (2/3) + Cronograma do Mês (1/3) */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-3 items-start">
              {/* Grade Mensal Ampliada */}
              <Card className="xl:col-span-2 p-3 sm:p-4">
                <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-100 dark:border-gray-800">
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100">
                      {`${MESES[currentMonth]} de ${currentYear}`}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Clique em qualquer dia para visualizar detalhes ou gerenciar eventos
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => navigateMonthStep(-1)}
                      title="Mês anterior"
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-xl bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs font-medium transition-colors"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      <span className="hidden sm:inline">Mês Ant.</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => navigateMonthStep(1)}
                      title="Próximo mês"
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-xl bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs font-medium transition-colors"
                    >
                      <span className="hidden sm:inline">Próx. Mês</span>
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Cabeçalho dos Dias da Semana */}
                <div className="grid grid-cols-7 gap-1.5 mb-2">
                  {DIAS_SEMANA.map((dia, idx) => (
                    <div
                      key={dia}
                      className={`text-center text-xs font-bold py-1 rounded-lg ${
                        idx === 0 || idx === 6
                          ? 'text-red-500/80 dark:text-red-400/80 bg-red-50/40 dark:bg-red-950/20'
                          : 'text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/40'
                      }`}
                    >
                      {dia}
                    </div>
                  ))}
                </div>

                {/* Células dos Dias do Mês */}
                <div className="grid grid-cols-7 gap-1.5">
                  {currentMonthDays.map((day, index) => {
                    if (!day) {
                      return (
                        <div
                          key={index}
                          className="min-h-[78px] sm:min-h-[96px] rounded-xl bg-gray-50/40 dark:bg-gray-900/20 border border-dashed border-gray-100 dark:border-gray-800/50"
                        />
                      );
                    }

                    const eventosDia = eventosDoDia(day.date);
                    const diaSemAula = eventosDia.find((ev) => ev.origem === 'feriado');
                    const isToday = day.date.toDateString() === hojeReferencia.toDateString();
                    const ehFimDeSemana = day.date.getDay() === 0 || day.date.getDay() === 6;

                    return (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleDayClick(day)}
                        title={eventosDia.map((ev) => ev.titulo).join(' · ') || undefined}
                        className={[
                          'group min-h-[78px] sm:min-h-[96px] w-full p-1.5 sm:p-2 rounded-xl text-left transition-all flex flex-col justify-between border relative overflow-hidden',
                          diaSemAula
                            ? 'bg-red-50/90 dark:bg-red-950/30 border-red-200 dark:border-red-800/60 hover:bg-red-100/80 dark:hover:bg-red-900/40'
                            : isToday
                              ? 'bg-blue-50/80 dark:bg-blue-950/40 border-blue-400 dark:border-blue-500 ring-2 ring-blue-500/30'
                              : ehFimDeSemana
                                ? 'bg-gray-50/70 dark:bg-gray-800/30 border-gray-200/60 dark:border-gray-800 hover:border-blue-300 dark:hover:border-blue-700'
                                : 'bg-white dark:bg-gray-900 border-gray-200/80 dark:border-gray-800 hover:border-blue-300 dark:hover:border-blue-700 hover:shadow-sm',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      >
                        <div className="flex items-center justify-between w-full">
                          <span
                            className={[
                              'inline-flex items-center justify-center w-6 h-6 rounded-lg text-xs font-bold transition-colors',
                              isToday
                                ? 'bg-blue-600 text-white shadow-sm'
                                : diaSemAula
                                  ? 'text-red-700 dark:text-red-400'
                                  : 'text-gray-800 dark:text-gray-200 group-hover:text-blue-600 dark:group-hover:text-blue-400',
                            ].join(' ')}
                          >
                            {day.date.getDate()}
                          </span>

                          {isToday && (
                            <span className="hidden sm:inline-block text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-blue-600/10 text-blue-600 dark:text-blue-400">
                              Hoje
                            </span>
                          )}
                        </div>

                        {/* Lista de Etiquetas dos Eventos do Dia */}
                        <div className="w-full space-y-1 mt-1">
                          {eventosDia.slice(0, 2).map((ev, i) => {
                            const cor = corDoEvento(ev);
                            return (
                              <div
                                key={i}
                                className={`w-full px-1.5 py-0.5 rounded-md border text-[10px] font-medium truncate flex items-center gap-1 ${cor.bgClaro} ${cor.texto} ${cor.borda}`}
                              >
                                <span
                                  className={`w-1.5 h-1.5 rounded-full shrink-0 ${cor.dot}`}
                                />
                                <span className="truncate">{ev.titulo}</span>
                              </div>
                            );
                          })}
                          {eventosDia.length > 2 && (
                            <div className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 pl-1">
                              +{eventosDia.length - 2} mais
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </Card>

              {/* Painel Lateral: Cronograma / Linha do Tempo do Mês */}
              <Card className="p-3 sm:p-4 flex flex-col max-h-[640px]">
                <div className="flex items-center justify-between gap-2 pb-3 border-b border-gray-100 dark:border-gray-800">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-xl bg-blue-600/10 dark:bg-blue-500/20 flex items-center justify-center text-blue-600 dark:text-blue-400">
                      <Filter className="w-4 h-4" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100">
                        Cronograma do Mês
                      </h3>
                      <p className="text-[11px] text-gray-500 dark:text-gray-400">
                        {cronogramaFiltrado.length} registro(s) em {MESES[currentMonth]}
                      </p>
                    </div>
                  </div>

                  {podeCriar && (
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={Plus}
                      onClick={() =>
                        handleDayClick({
                          date:
                            hojeReferencia.getFullYear() === currentYear &&
                            hojeReferencia.getMonth() === currentMonth
                              ? hojeReferencia
                              : new Date(currentYear, currentMonth, 1),
                        })
                      }
                    >
                      Agendar
                    </Button>
                  )}
                </div>

                {/* Pílulas de Filtro Rápido do Cronograma */}
                <div className="flex items-center gap-1 overflow-x-auto py-2.5 no-scrollbar border-b border-gray-100 dark:border-gray-800">
                  {[
                    { value: 'todos', label: 'Todos' },
                    { value: 'feriado', label: 'Sem Aula' },
                    { value: 'institucional', label: 'Institucional' },
                    ...AREAS.map((a) => ({ value: a.value, label: a.label })),
                  ].map((filtro) => {
                    const ativo = filtroCronograma === filtro.value;
                    return (
                      <button
                        key={filtro.value}
                        type="button"
                        onClick={() => setFiltroCronograma(filtro.value)}
                        className={`px-2.5 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap transition-colors ${
                          ativo
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                        }`}
                      >
                        {filtro.label}
                      </button>
                    );
                  })}
                </div>

                {/* Lista Cronológica */}
                <div className="flex-1 overflow-y-auto space-y-2 pr-1 pt-3">
                  {cronogramaFiltrado.length === 0 ? (
                    <div className="text-center py-10 px-4 space-y-2">
                      <CalendarDays className="w-8 h-8 text-gray-300 dark:text-gray-700 mx-auto" />
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                        Nenhum evento ou feriado registrado para este filtro em{' '}
                        {MESES[currentMonth]}.
                      </p>
                    </div>
                  ) : (
                    cronogramaFiltrado.map(({ date, evento }, idx) => {
                      const cor = corDoEvento(evento);
                      const isToday = date.toDateString() === hojeReferencia.toDateString();
                      return (
                        <button
                          key={`${evento.origem}-${evento.id}-${idx}`}
                          type="button"
                          onClick={() => handleDayClick({ date })}
                          className={`w-full text-left p-2.5 rounded-xl border transition-all hover:shadow-sm flex items-start gap-3 ${cor.bgClaro} ${cor.borda}`}
                        >
                          <div
                            className={`flex flex-col items-center justify-center min-w-[44px] py-1 px-1.5 rounded-lg bg-white/80 dark:bg-gray-900/80 border ${cor.borda}`}
                          >
                            <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400">
                              {DIAS_SEMANA[date.getDay()]}
                            </span>
                            <span className="text-sm font-extrabold text-gray-900 dark:text-gray-100 leading-tight">
                              {String(date.getDate()).padStart(2, '0')}
                            </span>
                          </div>

                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className={`text-[10px] font-bold uppercase ${cor.texto}`}>
                                {rotuloTipoEvento(evento)}
                              </span>
                              {isToday && (
                                <span className="text-[9px] font-bold px-1.5 py-0.2 rounded-full bg-blue-600 text-white">
                                  Hoje
                                </span>
                              )}
                            </div>
                            <p className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-gray-100 truncate mt-0.5">
                              {evento.titulo}
                            </p>
                            {evento.descricao && (
                              <p className="text-[11px] text-gray-600 dark:text-gray-400 line-clamp-2 mt-0.5">
                                {evento.descricao}
                              </p>
                            )}
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              </Card>
            </div>
          </>
        ) : (
          /* Visão Anual: Grade Clássica dos 12 Meses */
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2.5">
            {yearMonths.map((monthData) => {
              const ehMesCorrente =
                hojeReferencia.getFullYear() === currentYear &&
                hojeReferencia.getMonth() === monthData.month;
              return (
                <Card
                  key={monthData.month}
                  className={`p-2 transition-all ${
                    ehMesCorrente
                      ? 'ring-2 ring-blue-500/40 border-blue-300 dark:border-blue-700'
                      : ''
                  }`}
                >
                  <div className="flex items-center justify-between mb-1.5 px-1">
                    <button
                      type="button"
                      onClick={() => {
                        selectMonth(monthData.month);
                        setViewMode('mes');
                      }}
                      title="Abrir este mês na visão mensal"
                      className="text-xs font-bold text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    >
                      {MESES[monthData.month]}
                    </button>
                    {ehMesCorrente && (
                      <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400">
                        Mês Atual
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-7 gap-0.5 mb-0.5">
                    {DIAS_SEMANA.map((dia) => (
                      <div
                        key={dia}
                        className="text-center text-[9px] font-semibold text-gray-500 dark:text-gray-400 leading-tight"
                      >
                        {dia}
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-7 gap-0.5">
                    {monthData.days.map((day, index) => {
                      if (!day) return <div key={index} className="h-6" />;

                      const eventosDia = eventosDoDia(day.date);
                      const diaSemAula = eventosDia.find((ev) => ev.origem === 'feriado');
                      const isToday = day.date.toDateString() === hojeReferencia.toDateString();
                      return (
                        <button
                          key={index}
                          type="button"
                          onClick={() => handleDayClick(day)}
                          title={eventosDia.map((ev) => ev.titulo).join(' · ') || undefined}
                          className={[
                            'h-6 w-full rounded text-[9px] font-medium transition-all flex flex-col items-center justify-center gap-0.5 text-gray-700 dark:text-gray-300 hover:scale-105 hover:z-10 leading-none',
                            diaSemAula
                              ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800/50 font-semibold hover:bg-red-200 dark:hover:bg-red-900/50'
                              : '',
                            isToday && !diaSemAula
                              ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 ring-2 ring-blue-500 font-semibold'
                              : '',
                            !diaSemAula && !isToday
                              ? 'bg-gray-50 dark:bg-gray-800/60 border border-gray-100 dark:border-gray-800 hover:bg-gray-100 dark:hover:bg-gray-800'
                              : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                        >
                          <span>{day.date.getDate()}</span>
                          {eventosDia.length > 0 && (
                            <span className="flex items-center gap-0.5 h-1">
                              {eventosDia.slice(0, 3).map((ev, i) => (
                                <span
                                  key={i}
                                  className={`w-1 h-1 rounded-full ${corDoEvento(ev).dot}`}
                                />
                              ))}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* Legenda Unificada */}
        <Card className="flex flex-wrap items-center justify-center gap-4 p-2">
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded-md bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800/50" />
            <span className="text-xs text-gray-600 dark:text-gray-400">Dia Sem Aula</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded-md bg-blue-50 dark:bg-blue-900/30 ring-2 ring-blue-500" />
            <span className="text-xs text-gray-600 dark:text-gray-400">Hoje</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className={`w-2.5 h-2.5 rounded-full ${COR_INSTITUCIONAL.dot}`} />
            <span className="text-xs text-gray-600 dark:text-gray-400">Institucional</span>
          </div>
          {AREAS.map((a) => (
            <div key={a.value} className="flex items-center gap-1.5">
              <div
                className={`w-2.5 h-2.5 rounded-full ${CORES_AREA[a.value]?.dot || 'bg-gray-400'}`}
              />
              <span className="text-xs text-gray-600 dark:text-gray-400">{a.label}</span>
            </div>
          ))}
        </Card>

        <Modal
          isOpen={dayModalOpen}
          onClose={handleCloseDayModal}
          title={
            selectedDate &&
            selectedDate.toLocaleDateString('pt-BR', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })
          }
          maxWidth="md"
        >
          {!formOpen ? (
            <div className="space-y-3">
              {eventosDoDiaSelecionado.length === 0 ? (
                <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-6">
                  Nenhum evento neste dia.
                </p>
              ) : (
                <div className="space-y-2">
                  {eventosDoDiaSelecionado.map((ev, i) => {
                    const cor = corDoEvento(ev);
                    const chave = `${ev.origem}-${ev.id}`;
                    const temDescricao = !!ev.descricao;
                    const aberto = descricaoAbertaChave === chave;
                    return (
                      <div key={i} className={`rounded-xl border ${cor.bgClaro} ${cor.borda}`}>
                        <div className="flex items-center justify-between gap-2 p-2.5">
                          <button
                            type="button"
                            disabled={!temDescricao}
                            onClick={() => setDescricaoAbertaChave(aberto ? null : chave)}
                            className="min-w-0 flex-1 flex items-center gap-1.5 text-left"
                          >
                            <div className="min-w-0 flex-1">
                              <p className={`text-[11px] font-semibold ${cor.texto}`}>
                                {rotuloTipoEvento(ev)}
                              </p>
                              <p
                                className="text-sm text-gray-800 dark:text-gray-200 truncate"
                                title={ev.titulo}
                              >
                                {ev.titulo}
                              </p>
                            </div>
                            {temDescricao && (
                              <ChevronDown
                                className={`w-3.5 h-3.5 text-gray-400 flex-shrink-0 transition-transform ${aberto ? 'rotate-180' : ''}`}
                              />
                            )}
                          </button>
                          {podeAgirNoEvento(ev) && (podeEditar || podeExcluir) && (
                            <div className="flex gap-1 flex-shrink-0">
                              {podeEditar && (
                                <button
                                  type="button"
                                  onClick={() => abrirFormulario(ev)}
                                  className="p-1.5 rounded-lg hover:bg-white/70 dark:hover:bg-black/20 text-gray-600 dark:text-gray-300"
                                >
                                  <Pencil className="w-3.5 h-3.5" />
                                </button>
                              )}
                              {podeExcluir && (
                                <button
                                  type="button"
                                  onClick={() => handleExcluirEvento(ev)}
                                  className="p-1.5 rounded-lg hover:bg-white/70 dark:hover:bg-black/20 text-red-600 dark:text-red-400"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                        {aberto && temDescricao && (
                          <p className="px-2.5 pb-2.5 text-xs text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
                            {ev.descricao}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {podeCriar && (
                <Button
                  type="button"
                  variant="secondary"
                  icon={Plus}
                  onClick={() => abrirFormulario(null)}
                  className="w-full"
                >
                  Novo evento
                </Button>
              )}
            </div>
          ) : (
            <form onSubmit={handleSalvarEvento} className="space-y-3">
              {!editingEvento && (
                <Select
                  label="Tipo de evento"
                  value={formTipo}
                  onChange={(e) => setFormTipo(e.target.value)}
                  options={tiposEvento}
                />
              )}
              {formTipo === 'feriado' ? (
                <Input
                  label="Motivo"
                  value={formData.motivo}
                  onChange={(e) => setFormData({ ...formData, motivo: e.target.value })}
                  placeholder="Ex: Feriado, Reunião, etc."
                />
              ) : (
                <>
                  <Input
                    label="Título"
                    value={formData.titulo}
                    onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                    placeholder="Ex: Reunião de pais"
                  />
                  {/* Área/turma só são escolhidas na CRIAÇÃO — editar não troca nenhuma
                                        das duas (ver PUT em backend/agenda-eventos.js, que ignora esses
                                        campos de propósito), então na edição mostra só como texto fixo,
                                        pra não sugerir uma troca que não vai ter efeito nenhum. */}
                  {formTipo === 'area' &&
                    (editingEvento ? (
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Área:{' '}
                        <span className="font-medium text-gray-700 dark:text-gray-300">
                          {AREA_LABEL[formData.area] || formData.area}
                        </span>
                      </p>
                    ) : (
                      <Select
                        label="Área"
                        value={formData.area}
                        onChange={(e) => setFormData({ ...formData, area: e.target.value })}
                        options={areasPermitidas.map((a) => ({ value: a.value, label: a.label }))}
                      />
                    ))}
                  {formTipo === 'atividade' &&
                    (editingEvento ? (
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Turma:{' '}
                        <span className="font-medium text-gray-700 dark:text-gray-300">
                          {editingEvento.nome_atividade || '—'}
                        </span>
                      </p>
                    ) : (
                      <Select
                        label="Turma"
                        value={formData.id_atividade}
                        onChange={(e) => setFormData({ ...formData, id_atividade: e.target.value })}
                        options={[
                          { value: '', label: 'Selecione...' },
                          ...turmasPermitidas.map((t) => ({ value: String(t.id), label: t.nome })),
                        ]}
                      />
                    ))}
                  <Input
                    type="date"
                    label="Data fim (opcional, para eventos de vários dias)"
                    value={formData.data_fim}
                    onChange={(e) => setFormData({ ...formData, data_fim: e.target.value })}
                  />
                  <Input
                    label="Descrição (opcional)"
                    value={formData.descricao}
                    onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  />
                </>
              )}
              <div className="flex justify-end gap-2 pt-1">
                {editingEvento && podeExcluir && (
                  <Button
                    type="button"
                    variant="danger"
                    className="!bg-red-50 dark:!bg-red-900/30 !text-red-700 dark:!text-red-400 hover:!bg-red-100 dark:hover:!bg-red-900/50"
                    onClick={() => handleExcluirEvento(editingEvento)}
                  >
                    Remover
                  </Button>
                )}
                <Button type="button" variant="secondary" onClick={() => setFormOpen(false)}>
                  Voltar
                </Button>
                <Button type="submit" variant="primary">
                  Salvar
                </Button>
              </div>
            </form>
          )}
        </Modal>

        <Modal
          isOpen={isPeriodModalOpen}
          onClose={() => {
            setIsPeriodModalOpen(false);
            setPeriodData({ data_inicio: '', data_fim: '', motivo: '' });
          }}
          title="Marcar Período Sem Aula"
          maxWidth="md"
        >
          <form onSubmit={handleMarcarPeriodo} className="space-y-4">
            <Input
              type="date"
              label="Data Início"
              value={periodData.data_inicio}
              onChange={(e) => setPeriodData({ ...periodData, data_inicio: e.target.value })}
              required
            />
            <Input
              type="date"
              label="Data Fim"
              value={periodData.data_fim}
              onChange={(e) => setPeriodData({ ...periodData, data_fim: e.target.value })}
              required
            />
            <Input
              label="Motivo"
              value={periodData.motivo}
              onChange={(e) => setPeriodData({ ...periodData, motivo: e.target.value })}
              placeholder="Ex: Férias, Recesso, etc."
            />
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setIsPeriodModalOpen(false);
                  setPeriodData({ data_inicio: '', data_fim: '', motivo: '' });
                }}
              >
                Cancelar
              </Button>
              <Button type="submit" variant="primary" className="!bg-amber-500 hover:!bg-amber-600">
                Marcar Período
              </Button>
            </div>
          </form>
        </Modal>
      </main>
    </div>
  );
}

export default DiasSemAula;
